import { useEffect, useSyncExternalStore } from 'react';
import type { Editor } from '@tiptap/core';
import { Button } from 'primereact/button';
import { applyUserPreferences, browserUserPreferences } from '../general/user-preferences';

const EVENT = 'triad-word-completion-preference';
function enabled() { return browserUserPreferences().wordSuggestions; }
function subscribe(callback: () => void) {
    window.addEventListener(EVENT,callback); window.addEventListener('storage',callback);
    return () => { window.removeEventListener(EVENT,callback); window.removeEventListener('storage',callback); };
}
export function useWordCompletionEnabled() { return useSyncExternalStore(subscribe,enabled,() => false); }
export function WordCompletionToggle({ compact = false }: { compact?: boolean }) {
    const active = useWordCompletionEnabled();
    return <Button type="button" text={!active} size="small" icon="pi pi-language" label={compact ? undefined : `Word suggestions: ${active ? 'on' : 'off'}`}
        severity={active ? undefined : 'secondary'}
        aria-label={`British English word suggestions: ${active ? 'on' : 'off'}`} aria-pressed={active}
        title={`British English word suggestions: ${active ? 'on' : 'off'}. Suggestions appear after three letters. Use arrow keys to choose, then Enter to insert. Escape dismisses.`}
        onMouseDown={event => event.preventDefault()} onClick={() => {
            applyUserPreferences({ ...browserUserPreferences(), wordSuggestions: !active });
        }} />;
}
export function useEditorWordCompletion(editor: Editor | null) {
    const active = useWordCompletionEnabled();
    useEffect(() => {
        if (!active || !editor || editor.isDestroyed) return;
        let disposed = false; let detach: (() => void) | undefined;
        void import('../general/word-completion-tiptap').then(module => {
            if (!disposed && !editor.isDestroyed) detach = module.attachEditorWordCompletion(editor);
        }).catch(() => { /* A missing optional chunk must not interrupt editing. */ });
        return () => { disposed = true; detach?.(); };
    },[active,editor]);
}
export function useInputWordCompletion(doc: Document | null, readOnly: boolean) {
    const active = useWordCompletionEnabled();
    useEffect(() => {
        if (!active || !doc || readOnly) return;
        let disposed = false; let detach: (() => void) | undefined;
        void import('../general/word-completion-dom').then(module => { if (!disposed) detach = module.attachInputWordCompletion(doc); })
            .catch(() => { /* Optional enhancement; normal typing remains available. */ });
        return () => { disposed = true; detach?.(); };
    },[active,doc,readOnly]);
}
